// One sign-in for atlas.fernduft.com and fernduft.com.
//
// Both apps use the same Supabase project, but localStorage is per origin, so
// each kept its own session. On *.fernduft.com the session now lives in
// cookies on the parent domain, which both sites can read; elsewhere
// (localhost, Lovable previews) it stays in localStorage.
//
// KEEP IN SYNC: this file exists, identically, in atlas-foundation and
// joyfume-website (src/lib/sharedAuthStorage.ts). Change both or neither.

export const SHARED_AUTH_STORAGE_KEY = "fernduft.auth";

const SHARED_DOMAIN = "fernduft.com";
// Browsers cap a cookie at ~4 KB including its name and attributes; a session
// (two JWT-sized tokens plus the user object) is larger, so it is split.
const CHUNK_SIZE = 3000;
// Chrome's upper limit; the refresh token expires long before this anyway.
const MAX_AGE_SECONDS = 400 * 24 * 60 * 60;

/** The subset of Storage that supabase-js needs. */
export interface AuthStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Access to `document.cookie`, injectable so the chunking can be tested. */
export interface CookieJar {
  read(): string;
  write(cookie: string): void;
}

export function sharedCookieDomain(hostname: string): string | null {
  return hostname === SHARED_DOMAIN || hostname.endsWith(`.${SHARED_DOMAIN}`) ? SHARED_DOMAIN : null;
}

function parseCookies(header: string): Map<string, string> {
  const cookies = new Map<string, string>();
  for (const part of header.split(";")) {
    const eq = part.indexOf("=");
    if (eq < 0) continue;
    cookies.set(part.slice(0, eq).trim(), part.slice(eq + 1).trim());
  }
  return cookies;
}

/** Chunked cookie storage: `<key>.0`, `<key>.1`, … on `domain`. */
export function createCookieStorage(jar: CookieJar, domain: string, secure: boolean): AuthStorage {
  const attributes = `; Domain=${domain}; Path=/; SameSite=Lax${secure ? "; Secure" : ""}`;
  const expire = (name: string) => jar.write(`${name}=${attributes}; Max-Age=0`);

  return {
    getItem(key) {
      const cookies = parseCookies(jar.read());
      if (!cookies.has(`${key}.0`)) return null;
      let encoded = "";
      for (let i = 0; cookies.has(`${key}.${i}`); i++) encoded += cookies.get(`${key}.${i}`);
      try {
        return decodeURIComponent(encoded);
      } catch {
        return null;
      }
    },
    setItem(key, value) {
      const encoded = encodeURIComponent(value);
      const count = Math.max(1, Math.ceil(encoded.length / CHUNK_SIZE));
      for (let i = 0; i < count; i++) {
        const chunk = encoded.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        jar.write(`${key}.${i}=${chunk}${attributes}; Max-Age=${MAX_AGE_SECONDS}`);
      }
      // A shorter session than the last one leaves stale trailing chunks.
      const cookies = parseCookies(jar.read());
      for (let i = count; cookies.has(`${key}.${i}`); i++) expire(`${key}.${i}`);
    },
    removeItem(key) {
      const cookies = parseCookies(jar.read());
      for (let i = 0; cookies.has(`${key}.${i}`); i++) expire(`${key}.${i}`);
    },
  };
}

/** Where each app kept its session before this module existed. */
function legacyStorageKeys(supabaseUrl: string): string[] {
  const keys = ["joyfume.auth"]; // website
  try {
    keys.push(`sb-${new URL(supabaseUrl).hostname.split(".")[0]}-auth-token`); // Atlas (supabase-js default)
  } catch {
    /* ignore */
  }
  return keys;
}

/**
 * Moves a session from the app's old localStorage key into the shared store,
 * once, so nobody is signed out by the switch. The old keys are always
 * removed afterwards — left behind, they would bring a signed-out session
 * back on the next load.
 */
export function migrateLegacySession(storage: AuthStorage, local: Storage, legacyKeys: string[]): void {
  for (const legacyKey of legacyKeys) {
    const legacy = local.getItem(legacyKey);
    if (legacy === null) continue;
    if (storage.getItem(SHARED_AUTH_STORAGE_KEY) === null) storage.setItem(SHARED_AUTH_STORAGE_KEY, legacy);
    local.removeItem(legacyKey);
  }
}

let sharedStorage: AuthStorage | undefined;

/**
 * The storage to hand to supabase-js, with `storageKey: SHARED_AUTH_STORAGE_KEY`.
 * Undefined on the server, where supabase-js falls back to memory.
 */
export function createSharedAuthStorage(supabaseUrl: string): AuthStorage | undefined {
  if (typeof window === "undefined" || typeof document === "undefined") return undefined;
  if (sharedStorage) return sharedStorage;
  let local: Storage | null = null;
  try {
    local = window.localStorage;
  } catch {
    /* blocked */
  }
  const domain = sharedCookieDomain(window.location.hostname);
  const jar: CookieJar = {
    read: () => document.cookie,
    write: (cookie) => {
      document.cookie = cookie;
    },
  };
  sharedStorage = domain ? createCookieStorage(jar, domain, window.location.protocol === "https:") : local ?? undefined;
  if (sharedStorage && local) {
    try {
      migrateLegacySession(sharedStorage, local, legacyStorageKeys(supabaseUrl));
    } catch {
      /* ignore */
    }
  }
  return sharedStorage;
}

/** Signs this browser out of both sites without a network call ("remember me" off). */
export function clearSharedAuthSession(): void {
  try {
    sharedStorage?.removeItem(SHARED_AUTH_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
